import { type MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useHistory } from "react-router-dom";
import { type CloudAnalyticsModelWidgetModel, type Key } from "@doitintl/cmp-models";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { ModelTimestamp, WithModel } from "@doitintl/models-any";

import { useApiContext } from "../../../api/context";
import { reportsHelpURLs } from "../../../assets/urls";
import { useAuthContext } from "../../../Context/AuthContext";
import { useDashboardProcessingContext } from "../../../Context/DashboardProcessingContext";
import { refreshWidget } from "../../../Pages/CloudAnalytics/handlers/updateWidgets";
import { WidgetsContext } from "../../../Pages/Customer/NewDashboards/WidgetsGrid/WidgetsGrid";
import { consoleErrorWithSentry } from "../../../utils";
import { jsUcfirst } from "../../../utils/common";
import mixpanel from "../../../utils/mixpanel";
import { parseWidgetDocId } from "../../../utils/widgets";
import { useCustomerId } from "../../hooks/useCustomerId";
import useUnmountEffect from "../../hooks/useUnmountEffect";
import InfoMessageWidgetCard from "../InfoMessageWidgetCard";
import { SkeletonCard } from "../SkeletonCard";
import { type WidgetItemWithWidgetIdProps } from "../types";
import { WidgetMessageCardContent } from "../WidgetCards/Common/WidgetMessageCardContent";
import { CardReportHeader } from "./CardReportHeader";
import { CloudReportCardContent } from "./CloudReportCardContent";
import { CloudReportCardMenu } from "./CloudReportCardMenu";
import { CloudReportHeaderAction } from "./CloudReportHeaderAction";
import { useEnhancedWidgetData } from "./hooks";
import { ReportDeletedErrorCard } from "./ReportDeletedErrorCard";
import { cleanDashboardLensWidgetName } from "./utils";

type Props = Omit<WidgetItemWithWidgetIdProps, "fallbackComponent"> & {
  cloudAnalyticsKey?: Key;
  reportWithData?: WithModel<CloudAnalyticsModelWidgetModel>;
};

export default function CloudReportCard({ widgetId, cloudAnalyticsKey, reportWithData, widgetHeight = 500 }: Props) {
  const api = useApiContext();
  const history = useHistory();
  const [, , disableAutoRefresh] = useContext(WidgetsContext);

  const { currentUser, isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const customerId = useCustomerId();
  const [selectedFilter, setSelectedFilter] = useState<string>("All");
  const timeoutId = useRef<any>(-1);

  const { data, loading: widgetDocLoading } = useEnhancedWidgetData(widgetId, reportWithData);
  const showHeader = !reportWithData;
  const rawReport = data?.rawReport ?? undefined;
  const reportHasData = Boolean(rawReport?.rawRows?.length);
  const [handledAutoRefresh, setHandledAutoRefresh] = useState(disableAutoRefresh);
  const finishedLoadingData = (data && !widgetDocLoading) || (handledAutoRefresh && !widgetDocLoading);

  const [largeReportError, setLargeReportError] = useState(false);

  const { collaborators, description, name, isPublic, reportId, timeRefreshed } = useMemo(
    () => ({
      collaborators: rawReport?.collaborators ?? [],
      description: rawReport?.description ?? "",
      name: rawReport?.name ?? "",
      isPublic: rawReport?.isPublic ?? false,
      reportId: rawReport?.reportId ?? "",
      timeRefreshed: rawReport?.timeRefreshed,
    }),
    [rawReport]
  );
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(-1);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { refreshTime, setDashboardWidgetStates } = useDashboardProcessingContext();

  useUnmountEffect(() => {
    clearTimeout(timeoutId.current);
  });

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const hasAccessToReport = useMemo(
    () => isDoitEmployee || isPublic || collaborators?.some((collaborator) => collaborator.email === currentUser.email),
    [isDoitEmployee, isPublic, collaborators, currentUser.email]
  );

  const trackFullReport = useCallback(() => {
    if (name === "[GKE Lens] Unallocated Resources by Cluster") {
      mixpanel.track("dashboards.gke-lens.cost-allocation-clusters");
    }
    handleCloseMenu();
    mixpanel.track("dashboard.widget.launch", { name });
  }, [name]);

  const callToRefresh = useCallback(async () => {
    if (refreshing || Date.now() - lastRefreshTime < 2000) {
      return;
    }

    setRefreshing(true);

    const { customerId, reportId } = parseWidgetDocId(widgetId);

    try {
      const response = await refreshWidget(api, customerId, reportId);
      setLastRefreshTime(Date.now());
      if (response.status === 200) {
        return true;
      } else {
        if (response.status === 507) {
          setLargeReportError(true);
        }
        consoleErrorWithSentry(response);
        return false;
      }
    } finally {
      setRefreshing(false);
    }
  }, [api, lastRefreshTime, refreshing, widgetId]);

  useEffect(() => {
    if (refreshTime && Date.now() - refreshTime < 1000) {
      callToRefresh();
    }
  }, [callToRefresh, refreshTime]);

  useEffect(() => {
    if (!data && !widgetDocLoading) {
      if (handledAutoRefresh) {
        setDashboardWidgetStates(widgetId, false);
      } else {
        callToRefresh().then((success) => {
          if (success) {
            timeoutId.current = setTimeout(() => {
              setHandledAutoRefresh(true);
              setDashboardWidgetStates(widgetId, false);
            }, 90 * 1000);
          } else if (success === false) {
            setHandledAutoRefresh(true);
          }
        });
      }
    }
  }, [
    callToRefresh,
    widgetDocLoading,
    handledAutoRefresh,
    data,
    setDashboardWidgetStates,
    widgetId,
    disableAutoRefresh,
  ]);

  useEffect(() => {
    if (data) {
      setHandledAutoRefresh(true);
      setDashboardWidgetStates(widgetId, true);
      clearTimeout(timeoutId.current);
    }
  }, [data, setDashboardWidgetStates, widgetId]);

  const reportPath = `/customers/${customerId}/analytics/reports/${reportId}`;
  const helpUrl = reportsHelpURLs[reportId];

  const handleOpenReport = useCallback(() => {
    history.push(reportPath, { prevPage: history.location.pathname });
  }, [history, reportPath]);

  const menu = useMemo(
    () =>
      showHeader ? (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
          }}
        >
          {refreshing ? (
            <CircularProgress disableShrink sx={{ mt: 1 }} color="primary" size={24} thickness={4.8} />
          ) : (
            <CloudReportCardMenu
              anchorEl={anchorEl}
              handleRefresh={callToRefresh}
              hasAccessToReport={hasAccessToReport}
              helpUrl={helpUrl}
              timeRefreshed={timeRefreshed as ModelTimestamp}
              loading={refreshing}
              open={open}
              reportPath={reportPath}
              thisReportHasData={reportHasData}
              trackFullReport={trackFullReport}
              handleCloseMenu={handleCloseMenu}
              handleOpenMenu={handleOpenMenu}
            />
          )}
        </Stack>
      ) : undefined,
    [
      showHeader,
      refreshing,
      anchorEl,
      callToRefresh,
      hasAccessToReport,
      helpUrl,
      timeRefreshed,
      open,
      reportPath,
      reportHasData,
      trackFullReport,
    ]
  );

  const onChangeFilter = useCallback(
    (e) => {
      e.stopPropagation();
      setSelectedFilter(e.target.value);
    },
    [setSelectedFilter]
  );

  if (!finishedLoadingData) {
    return <SkeletonCard widgetHeight={widgetHeight} />;
  }

  if (finishedLoadingData && !data) {
    if (largeReportError) {
      return (
        <InfoMessageWidgetCard
          name="cloudReports"
          widgetHeight={widgetHeight}
          title={name}
          subheader={jsUcfirst(description)}
          message="Report cannot be displayed due to number of data points"
        />
      );
    }
    return <ReportDeletedErrorCard title={name} subHeader={jsUcfirst(description)} widgetHeight={widgetHeight} />;
  }

  return (
    <Card>
      {showHeader && (
        <CardReportHeader
          action={
            <CloudReportHeaderAction
              onChangeFilter={onChangeFilter}
              selectedFilter={selectedFilter}
              rawReport={rawReport}
              cloudAnalyticsKey={cloudAnalyticsKey}
              menu={menu}
            />
          }
          name={cleanDashboardLensWidgetName(name)}
          onOpenReport={handleOpenReport}
          hasAccessToReport={hasAccessToReport}
          description={description}
          hasFilter={Boolean(cloudAnalyticsKey)}
        />
      )}

      <Box
        sx={{
          py: 0.5,
          height: widgetHeight,
        }}
      >
        {reportHasData ? (
          <CloudReportCardContent
            rawReport={rawReport}
            selectedFilter={selectedFilter}
            cloudAnalyticsKey={cloudAnalyticsKey}
          />
        ) : (
          <WidgetMessageCardContent
            widgetHeight={widgetHeight}
            message="This report’s configuration returned no results."
            extra={
              <Button sx={{ mt: 2 }} variant="outlined" onClick={handleOpenReport}>
                Open in Reports
              </Button>
            }
          />
        )}
      </Box>
    </Card>
  );
}
